/**
 * Overview — what this is, how the tabs fit together, and how it's built.
 *
 * Every other tab explains itself once you're in it; nothing used to say what
 * the whole thing is for before you got there. This is that page: a fixed
 * point new to nobody who already knows the tool, useful to anyone who
 * doesn't yet. It teaches nothing the other tabs don't already say, and reads
 * nothing live — it is the one screen that cannot go stale by drifting from
 * the data, only by drifting from the code, and it is checked against the
 * code each time it's touched.
 */

export function OverviewTab({ onGoTo }: { onGoTo: (tab: string) => void }) {
  return (
    <>
      <div className="panel">
        <h2>What Reach Out is</h2>
        <div className="panel-body">
          <p className="hint">
            Reach Out runs customer outreach as <strong>standing campaigns</strong>, not one-off
            blasts. A campaign holds a filter (who), copy and a system prompt (what), a style, a
            schedule (when we look), a cooldown (how long before we can write to the same person
            again), and a send policy. Every few minutes a scheduler ticks: it evaluates who's
            eligible today, skips anyone suppressed or in cooldown, composes and refines drafts,
            and stops there for review — or send, once a person has switched that on.
          </p>
          <p className="hint">
            <strong>Nothing sends by accident.</strong> A new campaign starts disabled. It cannot
            be enabled until a person approves its copy and prompt at the GUI, and editing copy,
            prompt, filter, style or schedule later withdraws that approval and disarms it again.
            Automatic dispatch is a second, separate switch only a person can arm. Nothing outside
            this GUI — including any AI agent connected over MCP — holds send or arm authority, in
            any configuration.
          </p>
        </div>
      </div>

      <div className="panel">
        <h2>Where to start</h2>
        <div className="panel-body">
          <p className="hint">
            Most work starts in one of two places, depending on whether the audience already
            exists:
          </p>
          <table>
            <tbody>
              <tr>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="linkish" onClick={() => onGoTo('filters')}>
                    Filters
                  </button>
                </td>
                <td className="hint">
                  Define who a campaign can reach — a named, reusable condition. Not exclusive: an
                  account can match several filters at once.
                </td>
              </tr>
              <tr>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="linkish" onClick={() => onGoTo('campaigns')}>
                    Campaigns
                  </button>
                </td>
                <td className="hint">
                  Home. Create or edit the standing program itself, and open a cycle to see or act
                  on one tick of it — compose, refine, review, send.
                </td>
              </tr>
              <tr>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="linkish" onClick={() => onGoTo('people')}>
                    People
                  </button>
                </td>
                <td className="hint">
                  Count a filter against real accounts, or look at a saved list a campaign already
                  reused. Runs as a background job — a full scan takes a minute or more.
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="panel">
        <h2>Every tab, briefly</h2>
        <div className="panel-body">
          <table>
            <tbody>
              <tr>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="linkish" onClick={() => onGoTo('accounts')}>
                    Accounts
                  </button>
                </td>
                <td className="hint">
                  Look up one account and see what the engine knows about it. Masked by default —
                  unmasking real customer data is an explicit toggle.
                </td>
              </tr>
              <tr>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="linkish" onClick={() => onGoTo('emails')}>
                    Emails
                  </button>
                </td>
                <td className="hint">
                  Every draft across every campaign and cycle, in one review backlog — the same
                  data as a cycle's own Review step, viewed campaign-agnostically.
                </td>
              </tr>
              <tr>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="linkish" onClick={() => onGoTo('styles')}>
                    Styles
                  </button>
                </td>
                <td className="hint">
                  Gallery of email visual presentations, each previewed live over a real email —
                  and each one says what it's wrong for, since every style looks fine in isolation.
                </td>
              </tr>
              <tr>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="linkish" onClick={() => onGoTo('library')}>
                    Library
                  </button>
                </td>
                <td className="hint">
                  Read-only view of every predefined copy fragment, its approval status, and which
                  data fields it depends on.
                </td>
              </tr>
              <tr>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="linkish" onClick={() => onGoTo('improvement')}>
                    Improvement
                  </button>
                </td>
                <td className="hint">
                  Declared A/B experiments and how each arm is doing, once real sending exists.
                  There is no "promote winner" button — a person edits fragments in git.
                </td>
              </tr>
              <tr>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button className="linkish" onClick={() => onGoTo('settings')}>
                    Settings
                  </button>
                </td>
                <td className="hint">
                  Scheduler status, delivery/bounce/unsubscribe reporting, the LLM model in use,
                  SES deliverability evidence, and which secrets are configured (never their
                  values).
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div className="panel">
        <h2>How it's built</h2>
        <div className="panel-body">
          <p className="hint">
            One process serves all three surfaces on one port: this UI at <span className="mono">/</span>,
            the REST API this UI calls at <span className="mono">/api</span>, and the same tools
            over MCP at <span className="mono">/mcp</span> for agents like Claude or Cursor. The
            API and MCP both go through the same library underneath, so an answer given to an
            agent and an answer given here cannot drift apart — and MCP carries no send or arm
            authority in any configuration, however it's configured.
          </p>
          <p className="hint">
            Reach Out keeps its own database for campaigns, filters, drafts and the send journal.
            The CRM itself is read through a separate, read-only role — this system never writes
            to it. Explore the diagram below (drag to pan, scroll to zoom, or open it full-size).
          </p>
        </div>
        <iframe
          title="Reach Out system overview diagram"
          src="/architecture.html"
          sandbox="allow-scripts allow-same-origin"
          style={{
            width: '100%',
            height: 720,
            border: 0,
            borderTop: '1px solid var(--line)',
            background: '#fff',
          }}
        />
        <div className="panel-body">
          <p className="hint caption">
            <a href="/architecture.html" target="_blank" rel="noreferrer">
              Open full-size in a new tab ↗
            </a>
          </p>
        </div>
      </div>
    </>
  )
}
